/* global page */

import { spring, parallel, tween } from 'popmotion';
import { gsap } from 'gsap/all';
import { Grab } from './Grab';
import { reach } from './reach';
import { GLManager } from './GLManager';

function Showcase(data, options = {}) {
    this.destroyed = false;
    this.GL = new GLManager(data);
    this.GL.createPlane();

    this.data = data;

    this.progress = 1;
    this.direction = 0;
    this.waveIntensity = 0.2;

    this.options = options;

    this.index = {
        target: 0,
        current: 0,
        initial: 0,
        scrollSize: window.innerHeight / 6,
        active: 0,
    };

    this.follower = {
        x: 0,
        y: 0,
    };

    this.followerSpring = null;
    this.slidesSpring = null;

    this.grab = new Grab({
        onGrabStart: this.onGrabStart.bind(this),
        onGrabMove: () => {},
        onGrabEnd: this.onGrabEnd.bind(this),
    });

    // this.GL.speed = 0.05;
    this.GL.scheduleLoop();

    this.GL.updateStickEffect({
        progress: this.progress,
        direction: this.direction,
        waveIntensity: this.waveIntensity,
    });
}

Showcase.prototype.destroy = function() {
    if (this.destroyed) {
        return;
    }

    if (this.GLStickPop) {
        this.GLStickPop.stop();
    }

    if (this.followerSpring) {
        this.followerSpring.stop();
        this.followerSpring = null;
    }

    this.GL.cancelLoop();
    this.GL.unmount();
    this.grab.destroy();
    this.grab = null;
    this.GL = null;
    this.destroyed = true;
};

Showcase.prototype.mount = function(container) {
    this.GL.mount(container);
};

Showcase.prototype.render = function() {
    this.GL.render();
};

function clamp(num, min, max) {
    return Math.max(min, Math.min(num, max));
}

Showcase.prototype.onMouseMove = function(ev) {
    if (!this.GL) {
        return;
    }

    if (this.followerSpring) {
        this.followerSpring.stop();
        this.followerSpring = null;
    }

    this.followerSpring = reach({
        from: { x: this.follower.x, y: this.follower.y },
        to: { x: ev.clientX, y: ev.clientY },
        velocity: { x: this.follower.vx, y: this.follower.vy },
        stiffness: 500,
        damping: 50,
        mass: 1,
    }).start({
        update: (position) => {
            const velocity = {
                x: position.x - this.follower.x,
                y: position.y - this.follower.y,
            };

            this.GL.updateRgbEffect({ position, velocity });
            this.follower = {
                x: position.x,
                y: position.y,
                vx: velocity.x,
                vy: velocity.y,
            };
        },
        complete: () => {
            this.GL.updateRgbEffect({
                position: this.follower,
                velocity: { x: 0, y: 0 },
            });
            this.follower.vx = 0;
            this.follower.vy = 0;
        },
    });
};

Showcase.prototype.onActiveIndexChange = function(index) {
    if (index === this.index.active) {
        return;
    }

    this.index.active = index;

    this.GL.updateTexture(index);
    if (this.textureProgressSpring) {
        this.textureProgressSpring.stop();
        this.textureProgressSpring = null;
    }

    this.textureProgressSpring = tween({
        from: 0,
        to: 1,
        duration: 600,
    }).start((val) => {
        this.GL.updateTexture(null, val);
    });
};

Showcase.prototype.onGrabStart = function() {
    if (this.options.onZoomOutStart) {
        this.options.onZoomOutStart({ activeIndex: this.index.active });
    }

    this.index.initial = this.index.current;
    this.GL.speed = 0.08;
    document.body.classList.add('grabbing');

    gsap.set('.slide.show-meta a', { opacity: 0 });
    gsap.to('.slide h1', { duration: 0.4, z: -200, ease: 'power4.easeOut' });
    gsap.to('.slider__pagination', { duration: 0.3, autoAlpha: 0, ease: 'power4.easeOut' });
    gsap.to('.slider__pagination-divider', {
        duration: 0.3, scaleX: 0, transformOrigin: 'center center ', ease: 'power4.easeOut',
    });

    if (this.GLStickPop) {
        this.GLStickPop.stop();
    }
    this.GL.scheduleLoop();
    const directionSpring = spring({
        from: this.progress === 0 ? 0 : this.direction,
        to: 0,
        mass: 1,
        stiffness: 800,
        damping: 2000,
    });
    const progressSpring = spring({
        from: this.progress,
        to: 1,
        mass: 5,
        stiffness: 350,
        damping: 500,
    });

    const waveIntensitySpring = spring({
        from: this.waveIntensity,
        to: 0.2,
        mass: 3,
        stiffness: 500,
        damping: 2400,
    });
    this.GLStickPop = parallel(
        progressSpring,
        directionSpring,
        waveIntensitySpring,
    ).start({
        update: (values) => {
            // if (this.progress !== values[0]) {
            //   return;
            // }
            this.progress = values[0];
            this.direction = values[1];
            this.waveIntensity = values[2];

            this.GL.updateStickEffect({
                progress: this.progress,
                direction: this.direction,
                waveIntensity: this.waveIntensity,
            });
        },
        complete: () => {
            if (this.options.onZoomOutFinish) {
                this.options.onZoomOutFinish({ activeIndex: this.index.active });
            }
        },
    });
};

Showcase.prototype.onReadMore = function(url) {
    if (this.options.onZoomOutStart) {
        this.options.onZoomOutStart({ activeIndex: this.index.active });
    }

    // const { currentTarget } = event;

    // this.slides.appear();
    this.index.initial = this.index.current;
    const onComplete = () => {
        this.destroy();
        page.show(url);
    };

    gsap.to('.slides', 0.3, { autoAlpha: 0, y: 10, ease: 'power4.easeOut' });
    gsap.to('canvas', 0.2, {
        autoAlpha: 0, delay: 0.5, ease: 'power4.easeOut', onComplete,
    });

    gsap.to('.slider__pagination', 0.3, { autoAlpha: 0, ease: 'power4.easeOut' });
    gsap.to(['.footnote', '.footer-links'], 0.3, { autoAlpha: 0, y: 5, ease: 'power4.easeOut' });
    gsap.to('.slider__pagination-divider', 0.3, { scaleX: 0, transformOrigin: 'center center ', ease: 'power4.easeOut' });

    gsap.delayedCall(0.3, () => {
        if (this.GLStickPop) {
            this.GLStickPop.stop();
        }

        this.GL.speed = 0.002;

        this.GL.scheduleLoop();
        const directionSpring = spring({
            from: this.progress === 0 ? 0 : this.direction,
            to: 0,
            mass: 1,
            stiffness: 800,
            damping: 2000,
        });

        const progressSpring = spring({
            from: this.progress,
            to: 1,
            mass: 5,
            stiffness: 350,
            damping: 500,
        });

        const waveIntensitySpring = spring({
            from: this.waveIntensity,
            to: 0.2,
            mass: 3,
            stiffness: 50,
            damping: 1200,
        });

        this.GLStickPop = parallel(
            progressSpring,
            directionSpring,
            waveIntensitySpring,
        ).start({
            update: (values) => {
                // if (this.progress !== values[0]) {
                // }
                this.progress = values[0];
                this.direction = values[1];
                this.waveIntensity = values[2];

                this.GL.updateStickEffect({
                    progress: this.progress,
                    direction: this.direction,
                    waveIntensity: this.waveIntensity,
                });
            },
            complete: () => {
                if (this.options.onZoomOutFinish) {
                    this.options.onZoomOutFinish({ activeIndex: this.index.active });
                }
            },
        });
    });
};

Showcase.prototype.onReadLess = function() {
    if (this.options.onFullscreenStart) {
        this.options.onFullscreenStart({ activeIndex: this.index.active });
    }
    // this.slides.disperse(this.index.active);

    this.snapCurrentToActiveIndex();
    this.GL.speed = 0.05;

    gsap.to('canvas', 0.4, { scale: 1, delay: 0.2 });
    gsap.to('canvas', 0.3, { autoAlpha: 1, delay: 0.3 });

    if (this.GLStickPop) {
        this.GLStickPop.stop();
    }
    const directionSpring = spring({
        from: this.progress === 1 ? 1 : this.direction,
        to: 1,
        mass: 1,
        stiffness: 800,
        damping: 2000,
    });
    const progressSpring = spring({
        from: this.progress,
        to: 0,
        mass: 4,
        stiffness: 400,
        damping: 70,
        restDelta: 0.0001,
    });
    const waveIntensitySpring = spring({
        from: this.waveIntensity,
        to: 0.05,
        mass: 0.1,
        stiffness: 800,
        damping: 50,
    });

    this.GLStickPop = parallel(
        progressSpring,
        directionSpring,
        waveIntensitySpring,
    ).start({
        update: (values) => {
            this.progress = values[0];
            this.direction = values[1];
            this.waveIntensity = values[2];
            this.GL.updateStickEffect({
                progress: this.progress,
                direction: this.direction,
                waveIntensity: this.waveIntensity,
            });
        },
        complete: () => {
            if (this.options.onFullscreenFinish) {
                this.options.onFullscreenFinish({ activeIndex: this.index.active });
            }
            setTimeout(() => {
                // this.GL.cancelLoop();
                if (this.GLStickPop) {
                    this.GLStickPop.stop();
                }
            }, 1000);
        },
    });
};

Showcase.prototype.snapCurrentToActiveIndex = function() {
    if (this.slidesPop) {
        this.slidesPop.stop();
    }

    this.slidesPop = reach({
        from: { index: this.index.current },
        to: { index: Math.round(this.index.target) },
        restDelta: 0.001,
    }).start({
        complete: () => {
            gsap.to('.slide.show-meta a', 0, { opacity: 1 });
            gsap.to('.slide.show-meta .slider__pagination-holder', 0, { opacity: 1 });
            gsap.to('.slide.show-meta .slider__pagination', 0.3, { autoAlpha: 1, ease: 'power4.easeOut' });
            gsap.to('.slide.show-meta .slider__pagination-divider', 0.3, { scaleX: 1, transformOrigin: 'center center ', ease: 'power4.easeOut' });
        },
        update: (val) => {
            if (this.options.onIndexChange) {
                this.options.onIndexChange(val.index);
            }
            this.index.current = val.index;
        },
    });
};

Showcase.prototype.onGrabEnd = function() {
    if (this.options.onFullscreenStart) {
        this.options.onFullscreenStart({ activeIndex: this.index.active });
    }

    document.body.classList.remove('grabbing');

    this.snapCurrentToActiveIndex();
    this.GL.speed = 0.05;

    if (this.GLStickPop) {
        this.GLStickPop.stop();
    }

    gsap.to('.slide h1', { duration: 0.4, z: 0, ease: 'power4.easeOut' });

    const directionSpring = spring({
        from: this.progress === 1 ? 1 : this.direction,
        to: 1,
        mass: 1,
        stiffness: 800,
        damping: 2000,
    });

    const progressSpring = spring({
        from: this.progress,
        to: 0,
        mass: 4,
        stiffness: 400,
        damping: 70,
        restDelta: 0.0001,
    });

    const waveIntensitySpring = spring({
        from: this.waveIntensity,
        to: 0.05,
        mass: 0.1,
        stiffness: 800,
        damping: 50,
    });

    this.GLStickPop = parallel(
        progressSpring,
        directionSpring,
        waveIntensitySpring,
    ).start({
        update: (values) => {
            this.progress = values[0];
            this.direction = values[1];
            this.waveIntensity = values[2];
            this.GL.updateStickEffect({
                progress: this.progress,
                direction: this.direction,
                waveIntensity: this.waveIntensity,
            });
        },
        complete: () => {
            if (this.options.onFullscreenFinish) {
                this.options.onFullscreenFinish({ activeIndex: this.index.active });
            }

            if (this.GLStickPop) {
                this.GLStickPop.stop();
            }
            // setTimeout(() => {
            //     // this.GL.cancelLoop();
            //     if (this.GLStickPop) {
            //         this.GLStickPop.stop();
            //     }
            // }, 1000);
        },
    });
};

Showcase.prototype.onResize = function() {
    this.GL.onResize();
};

export { Showcase };
